<script setup lang="ts">
    import type { ISbStoryData } from '@storyblok/js';
    import { useStories } from '~/composables/storyblok-proxy/useStories';
    import { isPageNews } from '~/utils/isPageNews';

    const props = defineProps<{ blok: PageNewsReferenceStoryblok }>();

    const firstReference = computed(() => {
        const reference = props.blok.reference;

        if (!Array.isArray(reference)) {
            return null;
        }

        return reference[0] ?? null;
    });

    const referenceUuid = computed(() => {
        const first = firstReference.value;

        return typeof first === 'string' ? first : null;
    });

    // Storyblok inlines resolved relations as ISbStoryData (preview/draft with resolve_relations).
    // In that case reference[0] is an object, not a UUID — use it directly instead of a by_uuids fetch.
    const inlineResolvedReference = computed((): ISbStoryData<PageNewsStoryblok> | null => {
        const first = firstReference.value;

        if (!first || typeof first !== 'object') {
            return null;
        }

        return isPageNews(first) ? first : null;
    });

    const params = computed(() => ({
        ...(referenceUuid.value ? { by_uuids: referenceUuid.value } : {}),
        filter_query: {
            component: {
                in: 'PageNews'
            }
        }
    }));

    const referencedFetchOptions = computed(() => ({
        disabled: !referenceUuid.value
    }));

    const { data: referencedStories } = await useStories<PageNewsStoryblok>(params, referencedFetchOptions);

    const referencedStory = computed((): ISbStoryData<PageNewsStoryblok> | null => {
        if (inlineResolvedReference.value) {
            return inlineResolvedReference.value;
        }

        if (!referenceUuid.value) {
            return null;
        }

        const story = referencedStories.value?.[0] ?? null;

        return story && isPageNews(story) ? story : null;
    });

    if (!referencedStory.value) {
        throw createError({
            statusCode: 404,
            statusMessage: 'Page not found'
        });
    }

    const story = referencedStory.value;
</script>

<template>
    <PageNews
        :blok="story.content"
        :canonical-story="story"
    />
</template>
